import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { useLocation, useRoute, useRouter } from 'wouter';
import { useMessagingSocket } from '@/hooks/use-messaging-socket';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { ChevronLeft, Send, Loader2, MessageCircle } from 'lucide-react';
import { MobileHeader } from '@/components/ui/mobile-header';
import { EnhancedHeader } from '@/components/ui';

interface Message {
  id: number;
  conversationId: number;
  senderId: number;
  receiverId: number;
  content: string;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Conversation {
  id: number;
  user1Id: number;
  user2Id: number;
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: number;
  username: string;
}

export default function ConversationPage() {
  const [messageText, setMessageText] = useState('');
  const [localMessages, setLocalMessages] = useState<Message[]>([]);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [, params] = useRoute('/messages/conversation/:userId');
  const userId = params?.userId ? parseInt(params.userId) : null;
  
  // Initialize WebSocket for real-time messaging
  const { 
    isConnected, 
    isConnecting, 
    registerMessageCallback, 
    sendMessage 
  } = useMessagingSocket();

  // If no user ID is provided, redirect to messages page
  useEffect(() => {
    if (!userId) {
      navigate('/messages');
    }
  }, [userId, navigate]);

  // Set page title
  useEffect(() => {
    document.title = 'Conversation - Card Game Platform';
    return () => {
      document.title = 'Card Game Platform';
    };
  }, []);
  
  // Send authentication to WebSocket when connected
  useEffect(() => {
    if (isConnected && !isConnecting && user) {
      // Get token from localStorage, same as how API requests are handled
      const storedToken = localStorage.getItem('token');
      
      if (storedToken) {
        // Authenticate the WebSocket connection for messaging
        sendMessage({
          type: 'MESSAGING_AUTH',
          token: storedToken
        });
      }
    }
  }, [isConnected, isConnecting, user, sendMessage]);

  // Get or create conversation with the specified user
  const { data: conversationData, isLoading: loadingConversation } = useQuery({
    queryKey: ['/api/messages/conversation', userId],
    queryFn: async () => {
      if (!userId) throw new Error('No user ID provided');
      const response = await apiRequest('GET', `/api/messages/conversation/${userId}`);
      return await response.json();
    },
    enabled: !!userId && !!user,
    retry: false,
  });

  // Fetch messages for the conversation
  const { data: messages, isLoading: loadingMessages } = useQuery({
    queryKey: ['/api/messages/conversation', conversationData?.conversation?.id, 'messages'],
    queryFn: async () => {
      if (!conversationData?.conversation?.id) throw new Error('No conversation ID');
      const response = await apiRequest('GET', `/api/messages/conversation/${conversationData.conversation.id}/messages`);
      return await response.json();
    },
    enabled: !!conversationData?.conversation?.id,
    retry: false,
  });

  // Mark messages as read when viewing a conversation
  const markAsReadMutation = useMutation({
    mutationFn: async (conversationId: number) => {
      const response = await apiRequest('POST', `/api/messages/conversation/${conversationId}/read`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['/api/messages/unread-count'] 
      });
    },
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (messageData: {
      conversationId: number;
      senderId: number;
      receiverId: number;
      content: string;
    }) => {
      if (!messageData.conversationId) {
        throw new Error('Missing required information for sending message');
      }
      
      const response = await apiRequest(
        'POST', 
        `/api/messages/conversation/${messageData.conversationId}/messages`, 
        messageData
      );
      return await response.json();
    },
    onSuccess: (data) => {
      // Reset message input and invalidate messages query to refresh
      setMessageText('');
      queryClient.invalidateQueries({ 
        queryKey: ['/api/messages/conversation', conversationData?.conversation?.id, 'messages']
      });
      queryClient.invalidateQueries({ 
        queryKey: ['/api/messages/conversations'] 
      });
      
      // Auto-scroll to bottom after sending a message
      setTimeout(() => {
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
      }, 50);
    },
    onError: (error) => {
      toast({
        title: 'Error sending message',
        description: 'Message could not be sent. Please try again.',
        variant: 'destructive',
      });
      console.error('Error sending message:', error);
    }
  });

  // When a conversation is loaded, mark messages as read
  useEffect(() => {
    if (conversationData?.conversation?.id) {
      markAsReadMutation.mutate(conversationData.conversation.id);
    }
  }, [conversationData?.conversation?.id]);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (localMessages?.length && scrollAreaRef.current) {
      // Use setTimeout to ensure the DOM has updated before scrolling
      setTimeout(() => {
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [localMessages]);
  
  // Initialize messages from API data and ensure they're sorted by creation date
  useEffect(() => {
    if (messages) {
      // Sort messages by createdAt date in ascending order (oldest first)
      const sortedMessages = [...messages].sort((a, b) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      setLocalMessages(sortedMessages);
      
      // Auto-scroll to bottom when messages are first loaded
      setTimeout(() => {
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages]);
  
  // Set up WebSocket event listeners for real-time messaging
  useEffect(() => {
    if (!isConnected || !conversationData?.conversation?.id) return;
    
    // Handle new messages
    const unregisterNewMessage = registerMessageCallback('NEW_MESSAGE', (data) => {
      const newMessage = data.message;
      // Only update if the message belongs to the current conversation
      if (newMessage.conversationId === conversationData.conversation.id) {
        // Add message to our local state
        setLocalMessages((prevMessages) => {
          // Check if we already have this message (avoid duplicates)
          if (prevMessages.some(msg => msg.id === newMessage.id)) {
            return prevMessages;
          }
          
          // Add the new message and ensure messages are still sorted by timestamp
          const updatedMessages = [...prevMessages, newMessage];
          return updatedMessages.sort((a, b) => 
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        });
        
        // Mark the message as read if we're in the conversation
        if (newMessage.receiverId === user?.id) {
          markAsReadMutation.mutate(conversationData.conversation.id);
        }
        
        // Auto-scroll to bottom after a slight delay to ensure DOM updates
        setTimeout(() => {
          if (scrollAreaRef.current) {
            scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
          }
        }, 50);
      }
    });
    
    // Handle messages marked as read
    const unregisterMessageRead = registerMessageCallback('MESSAGE_READ', (data) => {
      // Update the read status of messages in this conversation
      if (data.conversationId === conversationData.conversation.id) {
        setLocalMessages((prevMessages) => 
          prevMessages.map(msg => 
            data.messageIds.includes(msg.id) ? { ...msg, isRead: true } : msg
          )
        );
      }
    });
    
    // Clean up event listeners
    return () => {
      unregisterNewMessage();
      unregisterMessageRead();
    };
  }, [isConnected, conversationData?.conversation?.id, user?.id, registerMessageCallback]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim() || !conversationData?.conversation?.id || !user?.id || !conversationData?.otherUser?.id) return;
    
    // Create complete message object with all required fields
    const messageData = {
      conversationId: conversationData.conversation.id,
      senderId: user.id,
      receiverId: conversationData.otherUser.id,
      content: messageText
    };
    
    sendMessageMutation.mutate(messageData, {
      onSuccess: () => {
        // Force an immediate scroll to the bottom after sending
        setTimeout(() => {
          if (scrollAreaRef.current) {
            scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
          }
        }, 50);
      }
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  if (!user) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p>Please log in to access messages</p>
      </div>
    );
  }

  if (loadingConversation) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const otherUser = conversationData?.otherUser;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-black/95 to-black/90 text-white flex flex-col">
      {/* Enhanced Header using consistent component */}
      <EnhancedHeader 
        title={otherUser ? otherUser.username : "Conversation"}
        backPath="/messages"
                coloredText={true}
                accentColor="text-primary"
                rightContent={
                  <div className="flex items-center text-xs text-muted-foreground">
                    <div 
                      className={`w-2 h-2 rounded-full mr-1.5 ${
                        isConnected ? 'bg-green-400' : isConnecting ? 'bg-yellow-400 animate-pulse' : 'bg-red-400'
                      }`} 
                    />

                    {
                    otherUser && (
                      <Avatar className="h-6 w-6 mr-2">
                        <AvatarFallback>
                          {otherUser.username.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    )
                  }
                  </div>
                  
                }
                
              />
      
      {/* Main content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="container max-w-2xl mx-auto px-4 py-4 flex-1 flex flex-col">
          {/* Messages Area */}
          <ScrollArea ref={scrollAreaRef} className="flex-1 p-4 mb-4 border border-white/10 rounded-lg bg-black/40 backdrop-blur-md">
            {loadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="flex flex-col gap-3 min-h-full pb-4">
                {!localMessages || localMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center min-h-[300px] py-8 px-4">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                        <MessageCircle className="h-10 w-10 text-primary" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary/20 animate-pulse"></div>
                      <div className="absolute -bottom-1 -left-1 w-6 h-6 rounded-full bg-primary/15 animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Start the conversation!</h3>
                    <p className="text-muted-foreground text-center max-w-xs mb-4">
                      Send a message to begin chatting with {otherUser.username}
                    </p>
                    <div className="max-w-xs w-full bg-primary/5 border border-primary/10 rounded-lg p-3 text-sm text-muted-foreground">
                      <p>Type your message below to get started</p>
                    </div>
                  </div>
                ) : (
                  [...localMessages].map((message: Message) => {
                    const isSentByMe = message.senderId === user?.id;
                    return (
                      <div
                        key={message.id}
                        className={`flex ${isSentByMe ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] px-4 py-3 rounded-2xl shadow-sm ${
                            isSentByMe
                              ? 'bg-primary text-primary-foreground rounded-br-sm'
                              : 'bg-muted rounded-bl-sm'
                          }`}
                        >
                          <p className="break-words">{message.content}</p>
                          <div className="flex items-center justify-between mt-1.5">
                            <p className={`text-xs ${isSentByMe ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                              {formatDate(message.createdAt)}
                            </p>
                            {isSentByMe && message.isRead && (
                              <span className="text-xs text-primary-foreground/70 ml-2">Read</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
            <ScrollBar />
          </ScrollArea>
        </div>
        
        {/* Message Input - Fixed at bottom */}
        <div className="sticky bottom-0 left-0 right-0 bg-black/80 backdrop-blur-md border-t border-white/10 py-3 px-4 z-10">
          <div className="container max-w-2xl mx-auto">
            <form onSubmit={handleSendMessage} className="flex items-center gap-2">
              <Input
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 bg-primary/5 border-primary/10 rounded-full px-4 py-2 focus-visible:ring-primary/30"
                autoComplete="off"
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={!messageText.trim() || sendMessageMutation.isPending}
                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground w-10 h-10 flex items-center justify-center"
              >
                {sendMessageMutation.isPending ? (
                  <div className="h-5 w-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}